import { useDashboardContext } from '@/contexts/DashboardContext/useDashboardContext';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { useSetEditor } from '@/contexts/SetEditorContext/useSetEditor';
import { SetsMode, SetsModeProvider } from '@/contexts/SetsModeContext';
import { View } from 'react-native';
import Card from '@/components/ui/therapist/Card';
import { Button } from '@/components/ui/therapist/Button';
import { Pagination } from '@/components/ui/therapist/Pagination';
import { NoSets } from '@/views/NoSets';
import { SetCard } from '@/views/SetCard';
import {
  SentenceSetFromApi,
  SetType,
  TextSetFromApi,
  WordSetFromApi,
} from '@/stores/store.types';
import { styles } from './styles';
import { SetFilter } from './SetFilter';
import { CombinedSet, useSetCardFilter } from './useSetCardFilter';
import SelectButton from '@/components/ui/therapist/SelectButton/SelectButton';
import { useStoreState } from '@/stores/store';
import { useSetsMode } from '@/contexts/SetsModeContext/useSetsMode';

const SETS_PER_PAGE = 6;

type PatientProfileSetsProps = {
  patientId: string;
  wordsets: WordSetFromApi[];
  sentencesets: SentenceSetFromApi[];
  textsets: TextSetFromApi[];
  refreshPatient: () => void;
};

const PatientProfileSetsContent = ({
  patientId,
  wordsets,
  sentencesets,
  textsets,
  refreshPatient,
}: PatientProfileSetsProps) => {
  const { fetchPatientsWrapper, selectedPatient } = useDashboardContext();
  const { mode, setMode } = useSetsMode();

  const permissions = useStoreState((state) => state.permissions);

  const { navigate } =
    useNavigation<StackNavigationProp<TherapistStackParamList>>();
  const { resetFormData } = useSetEditor();

  const selectedPatientFullName = useMemo(
    () =>
      selectedPatient
        ? `${selectedPatient?.firstName} ${selectedPatient?.lastName}`
        : '',
    [selectedPatient],
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [showFilter, setShowFilter] = useState(false);

  const combinedSets = useMemo<CombinedSet[]>(
    () =>
      [
        ...wordsets.map((data) => ({ data, type: 'Wörtersets' as SetType })),
        ...sentencesets.map((data) => ({
          data,
          type: 'Sätzesets' as SetType,
        })),
        ...(mode === 'aufgaben'
          ? textsets.map((data) => ({
              data,
              type: 'Textesets' as SetType,
            }))
          : []),
      ].sort((a, b) => (a.data.updatedAt > b.data.updatedAt ? -1 : 1)),
    [wordsets, sentencesets, textsets, mode],
  );

  const { filteredSets, filterProps, countFilterApplied } = useSetCardFilter(
    combinedSets,
    () => setCurrentPage(1),
  );

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredSets.length / SETS_PER_PAGE)),
    [filteredSets.length],
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedSets = useMemo(() => {
    const result = filteredSets.slice(
      (currentPage - 1) * SETS_PER_PAGE,
      currentPage * SETS_PER_PAGE,
    );
    return result;
  }, [filteredSets, currentPage]);

  const handleAddSetPress = useCallback(() => {
    resetFormData();
    navigate('SetCreation', { selectedID: patientId, flow: 'createSet' });
  }, [patientId, navigate, resetFormData]);

  return (
    <Card
      iconPosition="outside"
      iconCenterSize={44}
      icon="exerciseMaterial"
      title="Übungsmaterial"
    >
      <View style={styles.rowWithNegativeMargin}>
        <View style={styles.container}>
          <View style={styles.headerContainer}>
            {permissions.pictureCards ? (
              <SelectButton
                options={[
                  { label: 'Aufgaben', value: 'aufgaben' },
                  { label: 'Bildkarten', value: 'bildkarten' },
                ]}
                value={mode}
                onValueChange={(value) => setMode(value as SetsMode)}
              />
            ) : null}
            <View style={styles.headerActions}>
              <Button
                icon="filter"
                label={`Filter ${countFilterApplied > 0 ? `(${countFilterApplied})` : ''}`}
                look="secondary"
                onPress={() => {
                  setShowFilter(!showFilter);
                }}
              />
              <Button
                icon="plusCircle"
                label="Set erstellen"
                look="primary"
                onPress={handleAddSetPress}
              />
            </View>
          </View>
          {combinedSets.length === 0 || filteredSets.length === 0 ? (
            <NoSets
              label={
                combinedSets.length > 0 && filteredSets.length === 0
                  ? 'Kein passendes Set gefunden'
                  : 'Noch keine Sets vorhanden'
              }
              subLabel={
                combinedSets.length > 0 && filteredSets.length === 0
                  ? 'Ändern Sie die Filter oder erstellen Sie ein neues Set, um fortzufahren.'
                  : 'Erstellen Sie Ihr erstes Set, um zu starten.'
              }
            />
          ) : (
            <View style={styles.setContainer}>
              {paginatedSets.map(({ data, type }) => (
                <View key={data._id} style={styles.cardColumn}>
                  <SetCard
                    data={data}
                    type={type}
                    patientID={patientId}
                    selectedPatientFullName={selectedPatientFullName}
                    fetchPatientsWrapper={fetchPatientsWrapper}
                    refreshPatient={refreshPatient}
                  />
                </View>
              ))}
            </View>
          )}

          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </View>
      </View>
      <SetFilter
        isOpen={showFilter}
        onClose={() => setShowFilter(false)}
        {...filterProps}
        applyFilter={() => {
          filterProps.applyFilter();
          setShowFilter(false);
        }}
        resetFilter={() => {
          filterProps.resetFilter();
          setShowFilter(false);
        }}
      />
    </Card>
  );
};

export const PatientProfileSets = (props: PatientProfileSetsProps) => {
  return (
    <SetsModeProvider>
      <PatientProfileSetsContent {...props} />
    </SetsModeProvider>
  );
};
